import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { SafeRender } from "@/components/SafeRender";
import { useDialogueQorum, type DialogueAction } from "@/hooks/useDialogueQorum";

interface Props {
  conversationId: string;
  verdictText: string;
}

export interface DialogueQorumHandle {
  openWithAction: (action: DialogueAction, passage: string) => void;
}

const ACTIONS: { slug: DialogueAction; label: string; hint: string }[] = [
  { slug: "approfondir", label: "Approfondir", hint: "Aller plus loin sur ce point" },
  { slug: "expliquer", label: "Expliquer", hint: "Détailler le raisonnement" },
  { slug: "verifier", label: "Vérifier", hint: "Confronter aux sources" },
  { slug: "non_pertinent", label: "Non pertinent", hint: "Contester la pertinence" },
];

const ACTION_LABELS: Record<DialogueAction, string> = {
  approfondir: "Approfondir",
  expliquer: "Expliquer",
  verifier: "Vérifier",
  non_pertinent: "Non pertinent",
};

export const DialogueQorumDrawer = forwardRef<DialogueQorumHandle, Props>(
  function DialogueQorumDrawer({ conversationId, verdictText }, ref) {
  const [open, setOpen] = useState(false);
  const [activePassage, setActivePassage] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: 0 });
      });
    }
  }, [open]);
  const { history, loading, error, sendAction, reset } =
    useDialogueQorum(conversationId);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      reset();
      setActivePassage("");
    }
  };

  const handleAction = (action: DialogueAction) => {
    if (loading) return;
    void sendAction(action, activePassage || verdictText);
  };

  useImperativeHandle(
    ref,
    () => ({
      openWithAction(action, passage) {
        setActivePassage(passage);
        setOpen(true);
        void sendAction(action, passage);
      },
    }),
    [sendAction],
  );

  return (
    <>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="p-0"
          style={{
            background: "var(--ivory)",
            width: "100%",
            maxWidth: 480,
            borderLeft: "0.5px solid var(--stone-mid)",
            height: "100dvh",
            maxHeight: "100dvh",
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            wordBreak: "break-word",
          }}
          ref={scrollRef}
          data-testid="dialogue-qorum-drawer"
        >
          <div style={{ padding: "32px 28px 24px" }}>
            <SheetHeader style={{ textAlign: "left" }}>
              <SheetTitle
                style={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: 26,
                  color: "var(--charcoal)",
                  letterSpacing: "-0.01em",
                  margin: 0,
                }}
              >
                Le Dialogue
              </SheetTitle>
              <SheetDescription
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: "italic",
                  fontSize: 15,
                  color: "var(--smoke)",
                  marginTop: 4,
                }}
              >
                Approfondir avec le Conseil
              </SheetDescription>
            </SheetHeader>
          </div>

          <div
            style={{
              padding: "0 28px 20px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {ACTIONS.map((a) => (
              <button
                key={a.slug}
                type="button"
                disabled={loading}
                onClick={() => handleAction(a.slug)}
                data-testid={`dialogue-action-${a.slug}`}
                title={a.hint}
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: 14,
                  color: "var(--leather-deep)",
                  background: "#F5EDD8",
                  border: "0.5px solid var(--leather)",
                  borderRadius: 14,
                  padding: "14px 12px",
                  cursor: loading ? "wait" : "pointer",
                  opacity: loading ? 0.55 : 1,
                  transition: "all .2s ease",
                }}
                onMouseEnter={(e) => {
                  if (loading) return;
                  e.currentTarget.style.background = "var(--gold-tint)";
                  e.currentTarget.style.borderColor = "var(--gold-deep)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#F5EDD8";
                  e.currentTarget.style.borderColor = "var(--leather)";
                }}
              >
                {a.label}
              </button>
            ))}
          </div>

          {error ? (
            <div
              role="alert"
              style={{
                margin: "0 28px 16px",
                padding: "12px 14px",
                background: "rgba(107,26,43,0.06)",
                border: "0.5px solid var(--leather)",
                borderRadius: 8,
                fontFamily: '"DM Mono", monospace',
                fontSize: 12,
                color: "var(--leather-deep)",
              }}
            >
              {error}
            </div>
          ) : null}

          <div style={{ padding: "8px 28px 48px" }}>
            {history.length === 0 && !loading ? (
              <p
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: "italic",
                  fontSize: 15,
                  color: "var(--smoke)",
                  textAlign: "center",
                  margin: "32px 0",
                }}
              >
                Choisissez une action pour interroger le Conseil.
              </p>
            ) : null}

            {history.map((entry, i) => (
              <article
                key={entry.ts}
                data-testid="dialogue-entry"
                style={{
                  padding: "20px 0",
                  borderTop: i === 0 ? "none" : "0.5px solid var(--stone)",
                  display: "grid",
                  gridTemplateColumns: "40px 1fr",
                  gap: 14,
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "var(--charcoal-deep)",
                    color: "var(--accent-cramoisi)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 900,
                    fontSize: 14,
                  }}
                >
                  Q
                </div>
                <div>
                  <p
                    style={{
                      fontFamily: '"DM Mono", monospace',
                      fontSize: 10,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      color: "var(--leather)",
                      margin: "0 0 8px",
                    }}
                  >
                    {ACTION_LABELS[entry.action]}
                  </p>
                  <div
                    style={{
                      fontFamily: '"Playfair Display", serif',
                      fontSize: 15,
                      lineHeight: 1.6,
                      color: "var(--charcoal)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    <SafeRender
                      value={extractText(entry.response)}
                      asObject="compact"
                      fallback={
                        <SafeRender value={entry.response} asObject="verbose" />
                      }
                    />
                  </div>
                </div>
              </article>
            ))}

            {loading ? (
              <div
                role="status"
                aria-live="polite"
                style={{
                  padding: "24px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: "italic",
                  fontSize: 15,
                  color: "var(--smoke)",
                  borderTop:
                    history.length > 0 ? "0.5px solid var(--stone)" : "none",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "var(--gold-deep)",
                    animation: "qorumPulse 1.2s ease-in-out infinite",
                  }}
                />
                Le Conseil délibère…
              </div>
            ) : null}
          </div>

          <style>{`
            @keyframes qorumPulse {
              0%, 100% { opacity: .3; transform: scale(.8); }
              50% { opacity: 1; transform: scale(1.1); }
            }
          `}</style>
        </SheetContent>
      </Sheet>
    </>
  );
});


/**
 * Le backend peut renvoyer soit une string brute, soit un objet
 * { text } / { content } / { response } / { message } / { synthese, ... }.
 * On extrait la string si elle existe, sinon on retourne null pour
 * laisser SafeRender s'occuper du fallback verbose.
 */
function extractText(payload: unknown): unknown {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    const candidates = ["text", "content", "response", "message", "answer", "body"];
    for (const k of candidates) {
      if (typeof p[k] === "string" && (p[k] as string).trim()) return p[k];
    }
    // Si l'objet contient déjà une forme structurée connue de SafeRender, on le renvoie.
    if ("synthese" in p || "key_points" in p || "divergences" in p) return p;
  }
  return null;
}

export default DialogueQorumDrawer;
